/**
 * TTS audio playback via Web Audio API.
 *
 * `playBase64(b64)` — decodes and plays an inline MP3 reply. This is the
 * path the stage socket actually uses: `server.note.audio_b64` carries the
 * bytes on the socket that is already authenticated to carry them, rather
 * than a URL that would need storage, a lifetime, and an authorization
 * story of its own on the anonymous path.
 *
 * `playChunk(audioUrl)` — same playback over a fetched URL, kept for
 * `server.note.audio_url`, which remains in the wire type and is not
 * populated by any current server path.
 *
 * `cancel()` — stops current playback and discards queued chunks.
 *
 * Double-talk: if either play method is called while audio is already
 * playing, `onDoubleTalk` fires and `cancel()` is called automatically.
 */

interface TtsPlayerOptions {
  onDoubleTalk?: () => void;
}

export class TtsPlayer {
  private readonly opts: TtsPlayerOptions;
  private ctx: AudioContext | null = null;
  private currentSource: AudioBufferSourceNode | null = null;
  private playing = false;

  constructor(opts: TtsPlayerOptions = {}) {
    this.opts = opts;
  }

  /**
   * Decode and play an inline base64 audio payload.
   *
   * Decoded straight to bytes rather than handed to `fetch` as a `data:`
   * URL: a 20-second reply is a six-figure-character URL, and routing it
   * through the network stack to reach memory it is already in buys
   * nothing.
   */
  async playBase64(b64: string): Promise<void> {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return this._play(bytes.buffer);
  }

  /**
   * Fetch the audio at `audioUrl`, decode it, and play it.
   * If audio is currently playing, fires `onDoubleTalk` and cancels first.
   */
  async playChunk(audioUrl: string): Promise<void> {
    const response = await fetch(audioUrl);
    if (!response.ok) {
      throw new Error(`TtsPlayer: fetch failed ${response.status}`);
    }
    return this._play(await response.arrayBuffer());
  }

  /** Shared decode-and-play tail for both sources. */
  private async _play(arrayBuffer: ArrayBuffer): Promise<void> {
    if (this.playing) {
      this.opts.onDoubleTalk?.();
      this.cancel();
    }

    const context = this._getContext();
    const audioBuffer = await context.decodeAudioData(arrayBuffer);

    // After async work, check if we were cancelled in the meantime
    if (this.ctx === null) return;

    const source = context.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(context.destination);

    this.currentSource = source;
    this.playing = true;

    return new Promise<void>((resolve) => {
      source.onended = () => {
        if (this.currentSource === source) {
          this.currentSource = null;
          this.playing = false;
        }
        resolve();
      };
      source.start();
    });
  }

  /** Stop current playback immediately. */
  cancel(): void {
    this.playing = false;
    if (this.currentSource !== null) {
      try {
        this.currentSource.stop();
      } catch {
        // Already stopped — ignore
      }
      this.currentSource = null;
    }
    // Close context to release audio resources; will be recreated on next playChunk
    if (this.ctx !== null) {
      void this.ctx.close();
      this.ctx = null;
    }
  }

  // ─── private ──────────────────────────────────────────────────────────────

  private _getContext(): AudioContext {
    if (this.ctx === null || this.ctx.state === 'closed') {
      this.ctx = new AudioContext();
    }
    return this.ctx;
  }
}
